import WarningIcon from "@mui/icons-material/WarningAmber";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";
import {
  CostApportionment,
  CostStructure,
  FeeStructure,
} from "../../shared/CaseModel";
import { percentage } from "../../shared/percentage";
import { gainFill, TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { GuideHeader } from "../guide/GuideHeader.tsx";
import { GuideMarker } from "../guide/GuideMarker.tsx";
import { SplitBar } from "../general/SplitBar";
import { CostApportionmentSlider } from "./CostApportionmentsOfParty";

export interface CostApportionmentCallbacks {
  update(partyId: string, newValue: CostApportionment): Promise<void>;
}

export type CostApportionmentOfParty = {
  readonly partyId: string;
  partyName: string;
  apportionment?: CostApportionment;
  opponents: string[];
};

/**
 * The two costs in a fee are tints of the primary colour, the darker one for
 * the indirect costs. What remains of the fee is drawn as a gain: see-through,
 * so its text is the ink of the page.
 */
const INDIRECT_LIGHTEN = 0.2;
const DIRECT_LIGHTEN = 0.6;

export const LawFirmFees = () => {
  const caseContext = useCase();

  const submit = useCaseSubmit();

  const updateClientFeeStructure = (feeStructure: FeeStructure) => {
    const costStructure: CostStructure =
      caseContext.getCase().costStructure ?? {};
    costStructure.clientFeeStructure = feeStructure;
    return Promise.resolve(
      submit(
        { costStructure: JSON.stringify(costStructure) },
        { method: "put" },
      ),
    );
  };

  return (
    <LawFirmFeesComponent
      feeStructure={caseContext.getCase().costStructure?.clientFeeStructure}
      updateFeeStructure={updateClientFeeStructure}
    />
  );
};

export const LawFirmFeesComponent = ({
  feeStructure,
  updateFeeStructure,
}: {
  // undefined until the firm enters it: nothing is assumed meanwhile
  feeStructure: FeeStructure | undefined;
  updateFeeStructure: (newStructure: FeeStructure) => Promise<void>;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  // the shares as a slider is dragged, until the new structure is stored
  const [sliding, setSliding] = useState<Partial<FeeStructure>>({});

  // until a structure is entered the sliders rest at 0%; moving either one
  // enters both, the other at the 0% it rests at
  const entered =
    feeStructure !== undefined ||
    sliding.indirectCostsPercentage !== undefined ||
    sliding.directCostsPercentage !== undefined;
  const indirectCostsPercentage = feeStructure?.indirectCostsPercentage ?? 0;
  const directCostsPercentage = feeStructure?.directCostsPercentage ?? 0;

  const costsTotal = indirectCostsPercentage + directCostsPercentage;
  const margin = costsTotal < 1 ? 1 - costsTotal : 0;

  const save = (newStructure: FeeStructure) => {
    setSaving(true);

    updateFeeStructure(newStructure)
      .then(() => {
        setSaving(false);
        setError(undefined);
        setSliding({});
      })
      .catch((error) => {
        setSaving(false);
        console.warn("Failed to update cost structure", error);
        setError(error);
      });
  };

  // what the page shows: the stored shares, or those being dragged to
  const indirect = sliding.indirectCostsPercentage ?? indirectCostsPercentage;
  const direct = sliding.directCostsPercentage ?? directCostsPercentage;
  const profit = Math.max(0, 1 - indirect - direct);

  const parts = [
    {
      key: "indirectCosts",
      label: t("feeStructure.indirectCosts"),
      share: indirect,
      color: lighten(theme.palette.primary.main, INDIRECT_LIGHTEN),
      ink: theme.palette.getContrastText(
        lighten(theme.palette.primary.main, INDIRECT_LIGHTEN),
      ),
    },
    {
      key: "directCosts",
      label: t("feeStructure.directCosts"),
      share: direct,
      color: lighten(theme.palette.primary.main, DIRECT_LIGHTEN),
      ink: theme.palette.getContrastText(
        lighten(theme.palette.primary.main, DIRECT_LIGHTEN),
      ),
    },
    {
      key: "margin",
      label: t("feeStructure.margin"),
      share: profit,
      color: gainFill(theme),
      ink: theme.palette.text.primary,
    },
  ] as const;
  const [indirectPart, directPart, profitPart] = parts;

  return (
    <Stack spacing={2.25}>
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("feeStructure.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "72ch" }}
          >
            {t("feeStructure.info")}
          </Typography>
        </Box>
      </GuideHeader>

      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, overflow: "hidden", p: 2.5, userSelect: "none" }}
      >
        {/* the warning stands in the column of the profit it holds back */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            columnGap: { xs: 3, md: 5 },
            rowGap: 1.5,
            alignItems: "start",
          }}
        >
          <SectionLabel component="h2">
            {t("feeStructure.description")}
          </SectionLabel>
          {!entered && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
              <WarningIcon fontSize="small" color="warning" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("feeStructure.missing.title")}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("feeStructure.missing.text")}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {t("apportionments.error.updateFailed", { error })}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 3, md: 5 },
            alignItems: "center",
            mt: 2,
          }}
        >
          {/* The two shares that are set */}
          <Stack spacing={2.5}>
            <CostApportionmentSlider
              key={"indirectCostsPercentage_slider"}
              label={indirectPart.label}
              marker={<GuideMarker entry="indirect" />}
              color={indirectPart.color}
              secondaryLabel={t("feeStructure.secondaryLabel")}
              value={indirectCostsPercentage}
              max={Math.min(1, indirectCostsPercentage + margin)}
              caption={t("feeStructure.cap", {
                max: percentage.format(
                  Math.min(1, indirectCostsPercentage + margin),
                ),
              })}
              onUpdate={(newValue) => {
                // a share released where nothing was entered is entered
                if (newValue === feeStructure?.indirectCostsPercentage) return;
                save({
                  indirectCostsPercentage: newValue,
                  directCostsPercentage,
                });
              }}
              onSliding={(sliderValue) =>
                setSliding({ indirectCostsPercentage: sliderValue })
              }
              disabled={saving}
              notEntered={entered ? undefined : t("feeStructure.notEntered")}
            />
            <CostApportionmentSlider
              key={"directCostsPercentage_slider"}
              label={directPart.label}
              marker={<GuideMarker entry="direct" />}
              color={directPart.color}
              secondaryLabel={t("feeStructure.secondaryLabel")}
              value={directCostsPercentage}
              max={Math.min(1, directCostsPercentage + margin)}
              caption={t("feeStructure.cap", {
                max: percentage.format(
                  Math.min(1, directCostsPercentage + margin),
                ),
              })}
              onUpdate={(newValue) => {
                if (newValue === feeStructure?.directCostsPercentage) return;
                save({
                  directCostsPercentage: newValue,
                  indirectCostsPercentage,
                });
              }}
              onSliding={(sliderValue) =>
                setSliding({ directCostsPercentage: sliderValue })
              }
              disabled={saving}
              notEntered={entered ? undefined : t("feeStructure.notEntered")}
            />
          </Stack>

          {/* What they leave */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2.5,
              bgcolor: "background.default",
              px: 2.5,
              py: 2.25,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 0.75,
                  bgcolor: profitPart.color,
                  flex: "none",
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                {profitPart.label}
              </Typography>
              <GuideMarker entry="profit" />
            </Stack>
            <Typography
              variant="h3"
              component="p"
              sx={{
                fontWeight: 700,
                letterSpacing: "-.02em",
                lineHeight: 1.15,
                mt: 0.25,
                ...TABULAR_NUMERALS,
                ...(!entered && { color: "text.secondary" }),
              }}
            >
              {entered ? percentage.format(profit) : t("feeStructure.notSet")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("feeStructure.marginCaption")}
            </Typography>
          </Box>
        </Box>

        {/* The fee as one bar, empty until a structure is entered */}
        <Box sx={{ mt: 3 }}>
          <SplitBar parts={entered ? parts : []} />
        </Box>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", mt: 0.5 }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {percentage.format(0)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {percentage.format(1)}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
};
